import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Sliders, BadgeCheck, Play, X, Volume2, VolumeX } from 'lucide-react';
import Stars from './Stars';
import { LOOX_REVIEWS } from '../mock';
import { toast } from 'sonner';

const Highlight = ({ text, highlights = [] }) => {
  if (!highlights?.length) return <span>{text}</span>;
  let parts = [text];
  highlights.forEach((h) => {
    const next = [];
    parts.forEach((part) => {
      if (typeof part !== 'string') {
        next.push(part);
        return;
      }
      const idx = part.toLowerCase().indexOf(h.toLowerCase());
      if (idx === -1) {
        next.push(part);
        return;
      }
      next.push(part.slice(0, idx));
      next.push(
        <mark key={`${h}-${idx}`} className="review-highlight">
          {part.slice(idx, idx + h.length)}
        </mark>
      );
      next.push(part.slice(idx + h.length));
    });
    parts = next;
  });
  return <>{parts.map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)}</>;
};

const ReviewMedia = ({ review, onClick }) => {
  if (review.type === 'video') {
    return (
      <button onClick={onClick} className="relative w-full block group">
        <img
          src={review.poster}
          alt={review.name}
          className="w-full h-auto object-cover rounded-2xl"
        />
        <div className="absolute inset-0 rounded-2xl bg-black/15 group-hover:bg-black/25 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full border-2 border-white/90 flex items-center justify-center">
            <Play size={26} color="#fff" fill="#fff" />
          </div>
        </div>
      </button>
    );
  }
  if (review.type === 'image') {
    return (
      <img
        src={review.media}
        alt={review.name}
        className="w-full h-auto object-cover rounded-2xl"
      />
    );
  }
  return null;
};

const ReviewBody = ({ review }) => (
  <div
    className="p-4 rounded-2xl"
    style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
  >
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[14px] font-semibold" style={{ color: '#2C2418' }}>
        {review.name}
      </span>
      {review.verified && (
        <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: '#2D5A3D' }}>
          <BadgeCheck size={14} fill="#2D5A3D" color="#FFFFFF" />
          <span className="opacity-80">Verified</span>
        </span>
      )}
    </div>
    <div className="mb-2">
      <Stars rating={review.rating} size={14} color="#D9A441" />
    </div>
    <p className="text-[14px] leading-relaxed" style={{ color: '#2C2418' }}>
      <Highlight text={review.text} highlights={review.highlights} />
    </p>
  </div>
);

const VideoLightbox = ({ open, src, onClose }) => {
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white"
        aria-label="Close"
      >
        <X size={20} />
      </button>
      <div className="relative w-full max-w-[420px] aspect-[9/16] rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <video
          ref={videoRef}
          src={src}
          autoPlay
          loop
          muted={muted}
          playsInline
          className="w-full h-full object-cover bg-black"
        />
        <button
          onClick={() => setMuted((m) => !m)}
          className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/85 backdrop-blur flex items-center justify-center"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={18} color="#2D5A3D" /> : <Volume2 size={18} color="#2D5A3D" />}
        </button>
      </div>
    </div>
  );
};

const LooxReviews = () => {
  const [visibleCount, setVisibleCount] = useState(12);
  const [prevCount, setPrevCount] = useState(0); // index from which to animate
  const [lightbox, setLightbox] = useState({ open: false, src: '' });

  // Trigger entrance animation on first mount too
  useEffect(() => {
    setPrevCount(0);
  }, []);

  const reviews = LOOX_REVIEWS.slice(0, visibleCount);
  const overallRating = 4.8;

  const loadMore = () => {
    setPrevCount(visibleCount);
    setVisibleCount((c) => Math.min(LOOX_REVIEWS.length, c + 8));
  };

  return (
    <section id="reviews" className="mt-20">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Stars rating={overallRating} size={20} color="#D9A441" />
          <span className="text-[18px] font-medium" style={{ color: '#2C2418' }}>
            {overallRating}
          </span>
          <ChevronDown size={16} style={{ color: '#2C2418' }} />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toast('Reviews open in the original site only.')}
            className="px-5 py-3 rounded-full text-[14px] font-semibold btn-hover hover:opacity-95"
            style={{ backgroundColor: '#2D5A3D', color: '#FFFFFF' }}
          >
            Write a review
          </button>
          <button
            onClick={() => toast('Filters coming soon.')}
            className="w-12 h-12 rounded-full flex items-center justify-center btn-hover hover:opacity-95"
            style={{ backgroundColor: '#2D5A3D', color: '#FFFFFF' }}
            aria-label="Filters"
          >
            <Sliders size={18} />
          </button>
        </div>
      </div>

      {/* Masonry columns */}
      <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 md:gap-5 [column-fill:_balance]">
        {reviews.map((r, idx) => {
          const isNew = idx >= prevCount;
          const delay = isNew ? (idx - prevCount) * 90 : 0;
          return (
            <div
              key={r.id}
              className={`break-inside-avoid mb-4 md:mb-5 ${isNew ? 'review-card-enter' : ''}`}
              style={isNew ? { animationDelay: `${delay}ms` } : undefined}
            >
              {r.type !== 'text' && (
                <div className="mb-3">
                  <ReviewMedia
                    review={r}
                    onClick={r.type === 'video' ? () => setLightbox({ open: true, src: r.media }) : undefined}
                  />
                </div>
              )}
              <ReviewBody review={r} />
            </div>
          );
        })}
      </div>

      {visibleCount < LOOX_REVIEWS.length && (
        <div className="mt-10 text-center">
          <button
            onClick={loadMore}
            className="px-7 py-3 rounded-full text-[14px] font-semibold border-2 btn-hover hover:bg-[#2D5A3D] hover:text-white"
            style={{ borderColor: '#2D5A3D', color: '#2D5A3D', backgroundColor: 'transparent' }}
          >
            Show more reviews
          </button>
        </div>
      )}

      <VideoLightbox open={lightbox.open} src={lightbox.src} onClose={() => setLightbox({ open: false, src: '' })} />
    </section>
  );
};

export default LooxReviews;
